import React from 'react';
import { FiGlobe, FiCheckCircle, FiShield, FiZap, FiTrendingUp } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import SEO from '../components/SEO/SEO';

const Home: React.FC = () => {
  const navigate = useNavigate();

  const features = [
    {
      icon: <FiGlobe className="w-6 h-6" />,
      title: 'Global Coverage',
      description: '200+ countries with verified visa requirements and travel information',
      gradient: 'from-blue-500 to-cyan-500',
    },
    {
      icon: <FiShield className="w-6 h-6" />,
      title: 'Verified Sources',
      description: 'Direct links to official government portals and verified resources',
      gradient: 'from-green-500 to-emerald-500',
    },
    {
      icon: <FiZap className="w-6 h-6" />,
      title: 'Real-Time Updates',
      description: 'Always current information with automatic updates and monitoring',
      gradient: 'from-yellow-500 to-orange-500',
    },
    {
      icon: <FiTrendingUp className="w-6 h-6" />,
      title: 'Smart Analytics',
      description: 'Track travel requirements and get insights on visa trends',
      gradient: 'from-purple-500 to-pink-500',
    },
  ];

  const stats: never[] = [];

  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: 'travelbaba786',
    url: 'https://travelbaba786.com',
    description: 'Access official visa requirements, ETA information, and travel documentation for every country. Verified links to government e-visa portals worldwide.',
    publisher: {
      '@type': 'Organization',
      name: 'travelbaba786',
      url: 'https://travelbaba786.com',
      logo: {
        '@type': 'ImageObject',
        url: 'https://travelbaba786.com/logo..PNG',
      },
    },
    potentialAction: {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: 'https://travelbaba786.com/countries?search={search_term_string}',
      },
      'query-input': 'required name=search_term_string',
    },
    mainEntity: {
      '@type': 'ItemList',
      name: 'Countries with Travel Information',
      description: 'Comprehensive list of countries with visa requirements, ETA information, and official government portals',
      numberOfItems: '200+',
    },
  };

  return (
    <>
      <SEO
        title="travelbaba786 - Your Gateway to Global Travel Information"
        description="Access official visa requirements, ETA information, and travel documentation for every country. Verified links to government e-visa portals worldwide. Built for travelers, trusted by professionals."
        keywords="travel, visa, ETA, travel requirements, visa information, e-visa, government portal, travel documentation, visa application, travel API, country visa, travel guide, official visa portal"
        url="https://travelbaba786.com"
        structuredData={structuredData}
      />
      <div className="max-w-7xl mx-auto">
        {/* Hero Section */}
      <div className="text-center mb-24 px-6">
        {/* Badge */}
        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-8 border transition-all duration-300 hover:scale-105 cursor-default bg-blue-50 dark:bg-blue-500/10 border-blue-200 dark:border-blue-500/30 text-blue-600 dark:text-blue-300"
        >
          <FiShield className="w-4 h-4" />
          <span className="text-sm font-semibold">Verified & Trusted by Travelers Worldwide</span>
        </div>

        {/* Main Heading */}
        <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold mb-6 leading-tight text-gray-900 dark:text-white">
          Your Gateway to{' '}
          <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
            Global Travel
          </span>
        </h1>

        <p className="text-xl md:text-2xl mb-12 max-w-3xl mx-auto leading-relaxed text-gray-600 dark:text-gray-300">
          Access official visa requirements, ETA information, and travel documentation for every country.
          Built for travelers, trusted by professionals.
        </p>

        {/* CTA Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
          <button
            onClick={() => navigate('/countries')}
            className="group relative px-8 py-4 rounded-xl font-semibold text-base transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl flex items-center gap-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white hover:from-blue-700 hover:to-purple-700 dark:hover:from-blue-500 dark:hover:to-purple-500"
          >
            <span>Explore Countries</span>
            <span className="group-hover:translate-x-1 transition-transform">→</span>
          </button>

          <button
            onClick={() => navigate('/about')}
            className="px-8 py-4 rounded-xl font-semibold text-base transition-all duration-300 border-2 border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:border-gray-400 dark:hover:border-gray-600 hover:text-gray-900 dark:hover:text-white"
          >
            Learn More
          </button>
        </div>
      </div>

      {/* Stats Section removed per content update request */}

      {/* Features Section */}
      <div className="mb-24">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold mb-4 text-gray-900 dark:text-white">
            Why Choose{' '}
            <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
              travelbaba786?
            </span>
          </h2>
          <p className="text-lg text-gray-600 dark:text-gray-400">
            Everything you need to travel with confidence
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {features.map((feature, index) => (
            <div
              key={index}
              className="group relative p-8 rounded-2xl border transition-all duration-300 hover:scale-[1.02] hover:shadow-xl overflow-hidden bg-white dark:bg-slate-800/50 border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600"
            >
              {/* Gradient background on hover */}
              <div className={`absolute inset-0 bg-gradient-to-br ${feature.gradient} opacity-0 group-hover:opacity-5 transition-opacity duration-300`}></div>

              <div className="relative">
                {/* Icon */}
                <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${feature.gradient} flex items-center justify-center text-white mb-4 group-hover:scale-110 transition-transform duration-300`}>
                  {feature.icon}
                </div>

                <h3 className="text-xl font-bold mb-3 text-gray-900 dark:text-white">
                  {feature.title}
                </h3>

                <p className="leading-relaxed text-gray-600 dark:text-gray-300">
                  {feature.description}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* CTA Section */}
      <div className="relative rounded-3xl p-12 md:p-16 overflow-hidden border bg-gradient-to-br from-blue-600 to-purple-600 dark:from-blue-800 dark:to-purple-800 border-gray-700">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 right-0 w-96 h-96 bg-white rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-purple-500 rounded-full blur-3xl"></div>
        </div>

        <div className="relative text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Ready to Start Your Journey?
          </h2>
          <p className="text-lg text-white/90 mb-8 max-w-2xl mx-auto">
            Join thousands of travelers who trust our platform for accurate, up-to-date travel information
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => navigate('/countries')}
              className="px-8 py-4 bg-white text-gray-900 rounded-xl font-semibold hover:bg-gray-100 transition-all duration-300 transform hover:scale-105 shadow-lg flex items-center justify-center gap-2 group"
            >
              <span>Get Started Free</span>
              <span className="group-hover:translate-x-1 transition-transform">→</span>
            </button>

            <button
              onClick={() => navigate('/about')}
              className="px-8 py-4 bg-white/10 text-white border-2 border-white/20 rounded-xl font-semibold hover:bg-white/20 transition-all duration-300"
            >
              Learn How It Works
            </button>
          </div>
        </div>
      </div>
    </div>
    </>
  );
};

export default Home;